import MySwitchField from "@components/form/switch";
import { RHFAutocomplete, RHFTextField } from "@components/hook-form";
import { RHFNewUIUploadSingleFile } from "@components/RHFNewUIUploadSingleFile";
import usePermissions from "@hooks/usePermissions";
import {
  Alert,
  Box,
  Grid,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { IProcurementPOField } from "@pages/procurement/types/purchaseOrderTypes";
import { Plus } from "lucide-react";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { UseFieldArrayReturn, useFormContext } from "react-hook-form";
import { TaxRatesEntity } from "src/@types/catalog/taxRates";
import { TaxesModal } from "src/pages/Catalog/Taxes/components";
import { useCommonStyles } from "../hooks/useCommonStyles";

interface OptionItem {
  key: string;
  label: string;
  render: ReactNode;
}

interface IAdditionalOptionsProps {
  translate: (text: any, options?: any) => string;
  taxRates: TaxRatesEntity[] | null;
  taxes: UseFieldArrayReturn<any, "taxes", "id">;
  onTaxesRefresh: () => void;
  selectedComplianceStandard?: string | null;
  hiddenOptions?: string[];
  isFormReset: number;
  purchaseOrderERPFields?: IProcurementPOField[] | null;
  renderERPFields?: (fields: IProcurementPOField[]) => React.ReactNode;
}

const ToggleOption = ({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) => {
  const { inputContainer } = useCommonStyles();
  return (
    <Box
      sx={{
        ...inputContainer,
        boxShadow: "none",
        p: 1,
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <MySwitchField
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
          labelPlacement="end"
          label={label}
          sx={{ ml: 1, alignItems: "center" }}
        />
      </Box>
    </Box>
  );
};

export const AdditionalOptions = ({
  translate,
  taxRates,
  taxes,
  onTaxesRefresh,
  selectedComplianceStandard,
  hiddenOptions,
  isFormReset,
  purchaseOrderERPFields,
  renderERPFields,
}: IAdditionalOptionsProps) => {
  const theme = useTheme();
  const hiddenOptionSet = useMemo(
    () => new Set(hiddenOptions ?? []),
    [hiddenOptions],
  );
  const { permissionHandle } = usePermissions();
  const canWriteTax = permissionHandle(["Taxes_Write"]);
  const { inputContainer } = useCommonStyles();
  const { setValue, watch, getValues, clearErrors } = useFormContext<any>();
  const {
    documentClass,
    uploadedDocument,
    comments: commentsValue,
    totalDiscount,
  } = getValues();

  const discountType = watch("discountType");
  const comments = watch("comments");
  const isPOorDN =
    documentClass === "PurchaseOrder" || documentClass === "DeliveryNote";
  const taxesValue = (watch("taxes") ?? []) as Array<any>;
  const extraFieldsValue = watch("extraFields") as Record<string, any>;
  const hasExtraFieldsValue = Object.values(extraFieldsValue ?? {})?.some(
    (value) => value !== null && value !== undefined && value !== "",
  );

  const [enabled, setEnabled] = useState<Record<string, boolean>>({
    discount: !!totalDiscount,
    additionalTax: taxesValue.length > 0,
    comments: !!commentsValue,
    supportDocuments: !!uploadedDocument,
    additionalFields: hasExtraFieldsValue,
  });
  const [fileName, setFileName] = useState("");
  const [fileError, setFileError] = useState("");
  const [isTaxModalOpen, setIsTaxModalOpen] = useState(false);
  const items = (watch("items") ?? []) as Array<{
    dto?: number | null;
    vatPercentage?: number | null;
    taxType?: string | null;
  }>;
  const hasItemDto = items.some((item) => item?.dto != null && item.dto > 0);
  const hasItemTax = items.some(
    (item) => !!item?.taxType || (item?.vatPercentage ?? 0) > 0,
  );
  const selectedTaxId = watch("taxes.0.taxId");
  const selectedPOTaxType = watch("taxes.0.taxType");
  const selectedPOVatPercentage = watch("taxes.0.vatPercentage");

  const taxOptions = useMemo(() => {
    const availableRates = (taxRates ?? []).filter((rate) =>
      hasItemTax ? rate.isWithholdingTax : true,
    );
    return [
      ...availableRates.map(({ taxId, taxType, rate }) => ({
        value: taxId,
        label: `${taxType} (${rate}%)`,
      })),
      {
        value: "__add_new_tax__",
        label: "add_new_tax",
      },
    ];
  }, [taxRates, hasItemTax]);

  const poTaxTypeOptions = useMemo(() => {
    if (taxRates && taxRates.length > 0) {
      return [
        ...new Set(
          taxRates
            .filter((r) => (hasItemTax ? r.isWithholdingTax : true))
            .map((r) => r.taxType),
        ),
        "WHT",
      ].map((t) => ({ value: t, label: t }));
    }
    return ["WHT", "IRPF", "IGIC", "VAT"]
      .filter((t) => !hasItemTax || t === "WHT" || t === "IRPF")
      .map((t) => ({ value: t, label: t }));
  }, [taxRates, hasItemTax]);

  const poVatPercentageOptions = useMemo(() => {
    if (!taxRates || taxRates.length === 0 || selectedPOTaxType === "WHT")
      return null;
    return taxRates
      .filter((r) => r.taxType === selectedPOTaxType)
      .map((r) => ({ value: r.rate, label: `${r.description} (${r.rate}%)` }));
  }, [taxRates, selectedPOTaxType]);

  const addNewTaxLabel =
    translate("add_new_tax") === "add_new_tax"
      ? "Add New Tax"
      : translate("add_new_tax");

  const handleTaxCreated = () => {
    onTaxesRefresh();
  };

  const handleRemoveAttachment = useCallback(() => {
    setValue("uploadedDocument", null);
    setFileName("");
    setFileError("");
  }, [setValue]);

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        setFileError(translate("file_size_exceeds"));
        return;
      }
      setFileError("");
      setFileName(file.name);
      setValue(
        "uploadedDocument",
        Object.assign(file, { preview: URL.createObjectURL(file) }),
      );
    },
    [setValue, translate],
  );

  const options = useMemo<OptionItem[]>(
    () => [
      {
        key: "discount",
        label: translate("discount"),
        render: (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">
                {translate("discount")}
              </Typography>
            </Grid>
            {hasItemDto && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  {translate("item_level_discount_already_added")}
                </Alert>
              </Grid>
            )}
            {/* Hidden for now: Discount Type selection (always defaults to 'amount') */}
            <Grid item xs={6}>
              <RHFAutocomplete
                name="discountType"
                label={translate("discount_type")}
                disabled={hasItemDto}
                options={[
                  { value: "amount", label: translate("amount") },
                  { value: "percentage", label: translate("percentage") },
                ]}
              />
            </Grid>
            <Grid item xs={6}>
              <RHFTextField
                name="totalDiscount"
                label={translate("discount_amount")}
                size="small"
                disabled={hasItemDto}
                inputProps={{
                  inputMode: "decimal",
                  pattern: "[0-9]*",
                  min: 0,
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (
                    !/[0-9.]/.test(e.key) &&
                    ![
                      "Backspace",
                      "Delete",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Home",
                      "End",
                    ].includes(e.key)
                  ) {
                    e.preventDefault();
                  }
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  let raw = e.target.value.replace(/[^0-9.]/g, "");
                  const parts = raw.split(".");
                  if (parts.length > 2) {
                    raw = `${parts[0]}.${parts.slice(1).join("")}`;
                  }
                  setValue("totalDiscount", raw === "" ? null : (raw as any));
                }}
              />
            </Grid>
          </Grid>
        ),
      },
      {
        key: "additionalTax",
        label: translate("additional_tax"),
        render: (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">{translate("tax")}</Typography>
            </Grid>
            {hasItemTax && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  {translate("tax_info_not_required")}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <RHFTextField
                name="taxes.0.baseAmount"
                label={translate("base_amount")}
                size="small"
                type="number"
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Grid>
            {isPOorDN ? (
              <>
                <Grid item xs={12} sm={6}>
                  <RHFAutocomplete
                    name="taxes.0.taxType"
                    label={translate("tax_type")}
                    options={poTaxTypeOptions}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  {poVatPercentageOptions ? (
                    <RHFAutocomplete
                      name="taxes.0.vatPercentage"
                      label={translate("tax_percentage")}
                      options={poVatPercentageOptions}
                    />
                  ) : (
                    <RHFTextField
                      name="taxes.0.vatPercentage"
                      label={translate("tax_percentage")}
                      size="small"
                    />
                  )}
                </Grid>
              </>
            ) : (
              <Grid item xs={12} sm={6}>
                <RHFAutocomplete
                  name="taxes.0.taxId"
                  label={translate("tax")}
                  options={taxOptions}
                  getOptionDisabled={(option: any) =>
                    option.value === "__add_new_tax__" && !canWriteTax
                  }
                  onExternalChange={(newValue: any) => {
                    if (newValue?.value === "__add_new_tax__") {
                      if (canWriteTax) setIsTaxModalOpen(true);
                      return;
                    }
                    setValue("taxes.0.taxId", newValue?.value);
                  }}
                  renderOption={(props, option) => {
                    const isAddButton = option.value === "__add_new_tax__";
                    const isDisabled = isAddButton && !canWriteTax;

                    return (
                      <li
                        {...props}
                        key={option.value}
                        style={{
                          borderTop: isAddButton
                            ? `1px solid ${theme.palette.text.borderLine}`
                            : undefined,
                          opacity: isDisabled ? 0.4 : 1,
                        }}
                      >
                        {isAddButton ? (
                          <Tooltip
                            title={
                              isDisabled
                                ? translate("taxes_permission_required")
                                : ""
                            }
                            placement="right"
                          >
                            <span
                              style={{
                                pointerEvents: "all",
                                cursor: isDisabled ? "not-allowed" : "pointer",
                                width: "100%",
                              }}
                              onMouseDown={(e) => {
                                if (isDisabled) e.stopPropagation();
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{ alignItems: "center" }}
                              >
                                <Plus strokeWidth="1.5px" fontSize="20px" />
                                <Typography variant="body2">
                                  {translate(option.label)}
                                </Typography>
                              </Stack>
                            </span>
                          </Tooltip>
                        ) : (
                          translate(option.label)
                        )}
                      </li>
                    );
                  }}
                />
              </Grid>
            )}
          </Grid>
        ),
      },
      {
        key: "comments",
        label: translate("comments"),
        render: (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">
                {translate("comments")}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <RHFTextField
                name="comments"
                label={translate("comments")}
                size="small"
                multiline
                minRows={3}
              />
              <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
                <Typography
                  variant="subtitle2"
                  color={theme.palette.text.label}
                >
                  {comments?.length} / 500 {translate("characters")}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        ),
      },
      {
        key: "supportDocuments",
        label: translate("support_documents"),
        render: (
          <Stack spacing={2}>
            <Typography variant="subtitle1" color={theme.palette.text.label}>
              {translate("supporting_documents")}
            </Typography>
            {fileError && <Alert severity="error">{fileError}</Alert>}
            <RHFNewUIUploadSingleFile
              name="uploadedDocument"
              onDrop={handleDrop}
              dense
              handleDelete={handleRemoveAttachment}
              fileName={fileName}
            />
          </Stack>
        ),
      },
      ...(purchaseOrderERPFields && purchaseOrderERPFields.length > 0
        ? [
            {
              key: "additionalFields",
              label: translate("additional_fields"),
              render: (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">
                      {translate("additional_fields")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    {renderERPFields ? (
                      renderERPFields(purchaseOrderERPFields)
                    ) : (
                      <Alert severity="warning">
                        {translate("erp_fields_render_not_configured", {
                          defaultValue: "ERP field renderer not configured",
                        })}
                      </Alert>
                    )}
                  </Grid>
                </Grid>
              ),
            },
          ]
        : []),
    ],
    [
      translate,
      fileName,
      fileError,
      hasItemDto,
      hasItemTax,
      handleDrop,
      handleRemoveAttachment,
      taxOptions,
      isPOorDN,
      poTaxTypeOptions,
      poVatPercentageOptions,
      addNewTaxLabel,
      discountType,
      purchaseOrderERPFields?.length,
    ],
  );

  const visibleOptions = useMemo(
    () => options.filter((option) => !hiddenOptionSet.has(option.key)),
    [options, hiddenOptionSet],
  );

  const toggle = (key: string) => {
    const next = !enabled[key];
    setEnabled((prev) => ({ ...prev, [key]: next }));

    if (key === "discount") {
      setValue("isFormDiscountEnabled", next);
      if (!next) {
        setValue("totalDiscount", null);
        clearErrors("totalDiscount");
      }
    }
    if (key === "additionalTax") {
      if (next) {
        if (taxesValue.length === 0) {
          taxes.append({
            baseAmount: null,
            vat: 0,
            taxId: "",
            taxType: "",
            vatPercentage: 0,
            isWithHoldingTax: false,
          });
        }
      } else {
        taxes.remove();
        clearErrors("taxes");
      }
    }
  };

  // For Invoice/others: keep taxType/vatPercentage in sync with selected taxId.
  useEffect(() => {
    if (isPOorDN || !taxRates) return;
    if (taxesValue.length === 0) return;
    if (!selectedTaxId) {
      setValue("taxes.0.taxType", "");
      setValue("taxes.0.vatPercentage", 0);
      setValue("taxes.0.isWithHoldingTax", false);
      return;
    }
    const match = taxRates.find((rate) => rate.taxId === selectedTaxId);
    if (!match) return;
    setValue("taxes.0.taxType", match.taxType ?? "");
    setValue("taxes.0.vatPercentage", match.rate ?? 0);
    setValue("taxes.0.isWithHoldingTax", !!match.isWithholdingTax);
  }, [selectedTaxId, taxRates, taxesValue.length, setValue, isPOorDN]);

  useEffect(() => {
    if (!isPOorDN || taxesValue.length === 0) return;
    const matched = taxRates?.find(
      (r) =>
        r.taxType === selectedPOTaxType && r.rate === selectedPOVatPercentage,
    );
    setValue("taxes.0.taxId", matched?.taxId ?? null);
    if (selectedPOTaxType) {
      setValue(
        "taxes.0.isWithHoldingTax",
        selectedPOTaxType === "WHT" ? true : matched?.isWithholdingTax ?? false,
      );
    }
  }, [
    selectedPOTaxType,
    selectedPOVatPercentage,
    isPOorDN,
    taxesValue.length,
    taxRates,
    setValue,
  ]);

  useEffect(() => {
    if (enabled.discount) {
      setValue("discountType", "amount");
    }
  }, [enabled.discount, setValue]);

  useEffect(() => {
    if (taxesValue.length > 0) {
      setEnabled((prev) =>
        prev.additionalTax ? prev : { ...prev, additionalTax: true },
      );
    }
  }, [taxesValue.length]);

  useEffect(() => {
    if (hasExtraFieldsValue) {
      setEnabled((prev) =>
        prev.additionalFields ? prev : { ...prev, additionalFields: true },
      );
    }
  }, [hasExtraFieldsValue]);

  useEffect(() => {
    if (isFormReset > 0) {
      setValue("taxes", []);
      setEnabled({
        discount: false,
        additionalTax: false,
        comments: false,
        supportDocuments: false,
        additionalFields: false,
      });
    }
  }, [isFormReset]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">{translate("additional_options")}</Typography>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={1}>
          {visibleOptions.map((option) => {
            return (
              <Grid item xs={12} sm={6} md={3} key={option.key}>
                <ToggleOption
                  checked={enabled[option.key]}
                  onChange={() => toggle(option.key)}
                  label={option.label}
                />
              </Grid>
            );
          })}
        </Grid>
      </Grid>

      {visibleOptions.map(
        (option) =>
          enabled[option.key] && (
            <Grid item xs={12} key={`${option.key}-content`}>
              <Box sx={inputContainer}>{option.render}</Box>
            </Grid>
          ),
      )}
      {isTaxModalOpen && (
        <TaxesModal
          open={isTaxModalOpen}
          handleCloseModal={() => setIsTaxModalOpen(false)}
          onSuccess={handleTaxCreated}
          translate={translate}
          selectedComplianceStandard={selectedComplianceStandard ?? null}
          hasItemTaxFromManualForm={hasItemTax}
        />
      )}
    </Grid>
  );
};
